// MsspCompanyStep.js — MSSP Company details + address.
//
// Field layout (mirrors OrganizationStep in the standard signup):
//   Row 1. Company Name                (full width)
//   Row 2. Region   | Country          (Region hidden when the installation has one)
//   Row 3. Street Address              (full width, Google Places autocomplete)
//   Row 4. ZIP | State / Province | City  (3 cols — ZIP auto-fills State + City)
//   Row 5. Referral Source             (full width, optional)
//
// NOTE: useAddressAutocomplete fires onBatchChange with organization* keys
// (e.g. organizationAddress1). MsspSignup's handleBatchChange adapter
// translates them to the MSSP field names (address1, city, state, etc.)
// before updating formData — no changes to the shared hook are needed.

import React, { useRef, useEffect, useMemo } from 'react';
import Input from '../../Input';
import Select from '../../Select';
import { getAllCountryOptions, getStateOptions } from '../../../utils/countries';
import { shouldShowRegionPicker, regionCountryCodes } from '../../../utils/regions';
import { useGoogleMapsLoader } from '../../../hooks/useGoogleMapsLoader';
import { useAddressAutocomplete } from '../../../hooks/useAddressAutocomplete';

const REFERRAL_OPTIONS = [
  { value: '',                label: '-- Select an option --' },
  { value: 'search_engine',  label: 'Search Engine (Google, Bing, etc.)' },
  { value: 'social_media',   label: 'Social Media' },
  { value: 'colleague',      label: 'Colleague or Friend' },
  { value: 'conference',     label: 'Conference or Event' },
  { value: 'advertisement',  label: 'Advertisement' },
  { value: 'blog_article',   label: 'Blog or Article' },
  { value: 'partner_referral', label: 'Partner Referral' },
  { value: 'other',          label: 'Other' },
];

const MsspCompanyStep = ({
  formData,
  onChange,
  onBatchChange,       // already-translated adapter from MsspSignup
  errors,
  regions,             // array of { regionId, regionName } from MsspSignup
  onRegisterAsyncValidate,
}) => {
  const { isLoaded: googleIsLoaded, loadError: googleLoadError, isInactive: googleMapsInactive } =
    useGoogleMapsLoader();

  // ── Resolve the selected region + its address-autocomplete country codes ──
  const selectedRegion = regions.find((r) => String(r.regionId) === String(formData.regionId));
  const showRegionPicker = shouldShowRegionPicker(regions);

  // regionCodes MUST be memoized — a new array reference each render would
  // cause an infinite setState loop in useAddressAutocomplete's useEffect dep array.
  // Keyed on countryFilterKey (not the display name, which an operator may override).
  const regionCodes = useMemo(
    () => regionCountryCodes(selectedRegion),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRegion?.countryFilterKey]
  );

  // Country dropdown (all countries, not filtered by region)
  const countryOptions = getAllCountryOptions();

  // Region select options
  const regionOptions = useMemo(
    () => [
      { value: '', label: '-- Select Region --' },
      ...regions.map((r) => ({ value: r.regionId, label: r.regionName })),
    ],
    [regions]
  );

  // Pass MSSP field values; the hook reads them for debounced geocoding.
  // The hook fires onBatchChange with organization* keys — MsspSignup adapts them.
  const {
    suggestions,
    showSuggestions,
    setShowSuggestions,
    handleSuggestionSelect,
    zipLoading,
    validateAddress,
  } = useAddressAutocomplete({
    isLoaded:     googleIsLoaded,
    addressValue: formData.address1,
    country:      formData.country,
    regionCodes,
    zip:          formData.postalCode,
    city:         formData.city,
    state:        formData.state,
    onBatchChange,
  });

  // Register async validator so MsspSignup can call it on Next.
  useEffect(() => {
    if (onRegisterAsyncValidate) onRegisterAsyncValidate(validateAddress);
  }, [validateAddress, onRegisterAsyncValidate]);

  // Click-outside closes the address suggestion dropdown
  const addressWrapperRef = useRef(null);
  useEffect(() => {
    const close = (e) => {
      if (addressWrapperRef.current && !addressWrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [setShowSuggestions]);

  return (
    <div className="space-y-4">

      {/* Row 1 ── Company Name */}
      <Input
        label="Company Name"
        name="msspName"
        value={formData.msspName}
        onChange={onChange}
        required
        error={errors.msspName}
        placeholder="Enter your company name"
      />

      {/* Data Residency Region | Country — the region picker is hidden when the
          installation has one region (the wizard fills it in automatically) */}
      <div className={showRegionPicker ? 'grid grid-cols-2 gap-3' : ''}>
        {showRegionPicker && (
          <Select
            label="Data Residency Region"
            name="regionId"
            value={formData.regionId}
            onChange={onChange}
            options={regionOptions}
            error={errors.regionId}
            required
          />
        )}

        <Select
          label="Country"
          name="country"
          value={formData.country}
          onChange={onChange}
          options={countryOptions}
          error={errors.country}
          required
        />
      </div>
      {/* No region to pick (none loaded): surface the required-field error */}
      {!showRegionPicker && errors.regionId && (
        <p className="-mt-2 text-sm text-risk-critical">{errors.regionId}</p>
      )}

      {/* Street Address with Google Places autocomplete */}
      <div ref={addressWrapperRef} className="relative">
        <Input
          label="Street Address"
          name="address1"
          value={formData.address1}
          onChange={onChange}
          required
          error={errors.address1}
          placeholder={
            googleLoadError || googleMapsInactive ? 'Enter street address' : 'Start typing to search address…'
          }
          autoComplete="off"
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
        />

        {showSuggestions && suggestions.length > 0 && (
          <ul
            className="absolute z-50 w-full bg-white border border-[#d2d2d7] rounded-xl shadow-[0_4px_16px_rgba(0,0,0,0.10)] overflow-hidden"
            style={{ top: 'calc(100% - 1rem)', marginTop: '2px' }}
          >
            {suggestions.map((s, i) => (
              <li
                key={s.placePrediction?.placeId || i}
                className="border-b border-[#f0f0f0] last:border-b-0"
              >
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSuggestionSelect(s)}
                  className="w-full px-4 py-2.5 text-left hover:bg-[#f5f5f7] transition-colors"
                >
                  <p className="text-sm font-medium text-[#1d1d1f] truncate">
                    {s.placePrediction?.mainText?.text}
                  </p>
                  {s.placePrediction?.secondaryText?.text && (
                    <p className="text-xs text-[#6e6e73] truncate mt-0.5">
                      {s.placePrediction.secondaryText.text}
                    </p>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}

        {googleLoadError && (
          <p className="text-xs text-[#86868b] mt-1">
            Address autocomplete unavailable — please enter manually.
          </p>
        )}
      </div>

      {/* Row 4 ── ZIP | State / Province | City */}
      <div className="grid grid-cols-3 gap-3">

        {/* ZIP */}
        <div>
          <Input
            label="ZIP / Postal"
            name="postalCode"
            value={formData.postalCode}
            onChange={onChange}
            required
            error={errors.postalCode}
            placeholder="ZIP / Postal"
          />
          {zipLoading && (
            <p className="text-xs text-[#86868b] -mt-3 animate-pulse">
              Looking up…
            </p>
          )}
        </div>

        {/* State / Province */}
        {formData.country === 'US' ? (
          <Select
            label="State"
            name="state"
            value={formData.state}
            onChange={onChange}
            options={getStateOptions()}
            error={errors.state}
            required
          />
        ) : (
          <Input
            label="State / Province"
            name="state"
            value={formData.state}
            onChange={onChange}
            required
            error={errors.state}
            placeholder="State / Province"
          />
        )}

        {/* City */}
        <Input
          label="City"
          name="city"
          value={formData.city}
          onChange={onChange}
          required
          error={errors.city}
          placeholder="City"
        />
      </div>

      {/* Row 5 ── Referral Source (optional) */}
      <Select
        label="How did you hear about us?"
        name="referralSource"
        value={formData.referralSource}
        onChange={onChange}
        options={REFERRAL_OPTIONS}
        error={errors.referralSource}
      />

    </div>
  );
};

export default MsspCompanyStep;
